import { useMemo } from 'react';
import { Close } from '@carbon/react/icons';
import { OwnershipGraph, DocumentRecord } from './types';
import {
  buildLineageTree,
  LineageStep,
  ConsolidationSource,
} from './lineage-helpers';
import styles from './citation-tracing-panel.module.scss';

interface CitationTracingPanelProps {
  nodeId: string;
  graph: OwnershipGraph;
  documents: Record<string, DocumentRecord>;
  onClose: () => void;
}

function truncate(str: string | null, max: number): string {
  if (!str) return '';
  return str.length > max ? str.slice(0, max) + '…' : str;
}

function SourceSubRow({ source }: { source: ConsolidationSource }) {
  return (
    <div className={styles.consolidationSource}>
      <div className={styles.subRow}>
        <span className={styles.sourceArrow}>↳</span>
        <a
          href={`/entities/${source.entityId}`}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.entityName}
          onClick={(e) => e.stopPropagation()}
        >
          {source.entityName}
        </a>
        {source.documentId !== null && (
          <>
            <span className={styles.divider}>∨</span>
            <a
              href={`/documents/${source.documentId}`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.docLink}
              onClick={(e) => e.stopPropagation()}
              title={source.documentTitle ?? `Document #${source.documentId}`}
            >
              {truncate(
                source.documentTitle ?? `Document #${source.documentId}`,
                40
              )}
              {' ↗'}
            </a>
          </>
        )}
        <span className={styles.divider}>·</span>
        <span>{source.eventType}</span>
      </div>
      {source.children.length > 0 && (
        <div className={styles.children}>
          {source.children.map((child, i) => (
            <StepView key={i} step={child} />
          ))}
        </div>
      )}
    </div>
  );
}

function StepView({ step }: { step: LineageStep }) {
  if (step.isConsolidation && step.consolidationSources) {
    const sources = step.consolidationSources;
    return (
      <div className={styles.step}>
        <div className={styles.mathRow}>
          {sources.map((src, i) => (
            <span key={i}>
              {i > 0 && <span className={styles.multiplier}>{' + '}</span>}
              <span className={styles.fraction}>{src.fraction}</span>
            </span>
          ))}
          <span className={styles.arrow}>→</span>
          <span className={styles.toEntity}>{step.toEntityName}</span>
          <span className={styles.toFraction}>{step.toFraction}</span>
        </div>
        <div className={styles.consolidationSources}>
          {sources.map((src, i) => (
            <SourceSubRow key={i} source={src} />
          ))}
        </div>
      </div>
    );
  }

  const hasChildren = step.children.length > 0;

  return (
    <div className={styles.step}>
      <div className={styles.mathRow}>
        <span className={styles.fraction}>{step.fromFraction}</span>
        <span className={styles.multiplier}>×</span>
        <span className={styles.multiplier}>{step.multiplier}</span>
        <span className={styles.arrow}>→</span>
        <span className={styles.toEntity}>{step.toEntityName}</span>
        <span className={styles.toFraction}>{step.toFraction}</span>
      </div>
      {!step.depthCapped && (
        <div className={styles.subRow}>
          <a
            href={`/entities/${step.fromEntityId}`}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.entityName}
            onClick={(e) => e.stopPropagation()}
          >
            {step.fromEntityName}
          </a>
          {step.documentId !== null && (
            <>
              <span className={styles.divider}>∨</span>
              <a
                href={`/documents/${step.documentId}`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.docLink}
                onClick={(e) => e.stopPropagation()}
                title={step.documentTitle ?? `Document #${step.documentId}`}
              >
                {truncate(
                  step.documentTitle ?? `Document #${step.documentId}`,
                  40
                )}
                {' ↗'}
              </a>
            </>
          )}
          <span className={styles.divider}>·</span>
          <span>{step.eventType}</span>
        </div>
      )}

      {hasChildren && (
        <div className={styles.children}>
          {step.children.map((child, i) => (
            <StepView key={i} step={child} />
          ))}
        </div>
      )}

      {step.isEstablishmentRoot && (
        <>
          {step.depthCapped && (
            <div className={styles.ancestryOmitted}>… ancestry omitted</div>
          )}
          <div className={styles.establishmentRow}>
            <span className={styles.establishedBadge}>⊕</span>
            <span className={styles.fraction}>{step.fromFraction}</span>
            <a
              href={`/entities/${step.fromEntityId}`}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.entityName}
              onClick={(e) => e.stopPropagation()}
            >
              {step.fromEntityName}
            </a>
            <span className={styles.establishedLabel}>Established</span>
          </div>
        </>
      )}
    </div>
  );
}

export function CitationTracingPanel({
  nodeId,
  graph,
  documents,
  onClose,
}: CitationTracingPanelProps) {
  const steps = useMemo(
    () => buildLineageTree(nodeId, graph, documents),
    [nodeId, graph, documents]
  );

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <span className={styles.panelTitle}>Ownership Lineage</span>
        <button
          type="button"
          className={styles.closeBtn}
          onClick={onClose}
          aria-label="Close lineage panel"
        >
          <Close />
        </button>
      </div>
      {steps.length === 0 ? (
        <div className={styles.emptyState}>
          No incoming edges — this is an establishment (root) node.
        </div>
      ) : (
        <div className={styles.stepList}>
          {steps.map((step, i) => (
            <StepView key={i} step={step} />
          ))}
        </div>
      )}
    </div>
  );
}
